/**
 * LinkZoomMeetingDialog — attach an existing Zoom meeting to a virtual event.
 *
 * The organizer supplies the meeting ID; the attendee join link is then read back
 * from Zoom rather than assembled here. A passcode-protected meeting's real link
 * carries an embedded token, so a link built from the ID alone would look right in
 * this dialog and fail for every attendee who clicked it in their confirmation.
 */
import { useEffect, useState } from "react";
import { Video, X } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";
import { formatZoomMeetingId, normalizeZoomMeetingId } from "@shared/zoomLinks";

const labelStyle = {
  display: "block",
  margin: "0 0 .35rem",
  color: "#8FA0B6",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "var(--chi-body-compact)",
  fontWeight: 750,
  letterSpacing: ".08em",
  textTransform: "uppercase" as const,
};

const inputStyle = {
  width: "100%",
  // Without this the form's own padding pushes a 100%-width input past a 390px
  // viewport; the overflow was hidden only because the overlay is position:fixed.
  boxSizing: "border-box" as const,
  minHeight: 44,
  padding: ".6rem .7rem",
  border: "1px solid rgba(255,255,255,.12)",
  borderRadius: ".55rem",
  background: "rgba(255,255,255,.03)",
  color: "white",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "var(--chi-body-compact)",
};

const noteStyle = {
  margin: ".45rem 0 0",
  color: "#7F8BA0",
  fontFamily: "'Inter', sans-serif",
  fontSize: "var(--chi-body-compact)",
  lineHeight: 1.5,
};

export default function LinkZoomMeetingDialog({
  eventSlug,
  eventTitle,
  onClose,
}: {
  eventSlug: string;
  eventTitle: string;
  onClose: () => void;
}) {
  const [meetingIdInput, setMeetingIdInput] = useState("");
  const utils = trpc.useUtils();
  const currentMeeting = trpc.events.adminZoomMeeting.useQuery({ eventSlug }, { retry: false });

  const linkMeeting = trpc.events.linkZoomMeetingById.useMutation({
    onSuccess: async meeting => {
      await Promise.all([
        utils.events.adminZoomMeeting.invalidate({ eventSlug }),
        utils.events.myZoomJoinLink.invalidate(),
      ]);
      chiNotify.success("Zoom meeting linked", {
        description: `Registered attendees for ${eventTitle} can now reach meeting ${
          meeting.zoomMeetingId ? formatZoomMeetingId(meeting.zoomMeetingId) : "this Zoom meeting"
        }.`,
      });
      onClose();
    },
    onError: error => chiNotify.error("Zoom meeting not linked", { description: error.message }),
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !linkMeeting.isPending) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [linkMeeting.isPending, onClose]);

  const normalizedId = normalizeZoomMeetingId(meetingIdInput);
  const hasInput = meetingIdInput.trim().length > 0;
  const existing = currentMeeting.data;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!normalizedId || linkMeeting.isPending) return;
    linkMeeting.mutate({ eventSlug, zoomMeetingId: normalizedId });
  };

  return (
    <div
      role="presentation"
      onMouseDown={event => {
        if (event.target === event.currentTarget && !linkMeeting.isPending) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        display: "grid",
        placeItems: "center",
        padding: "clamp(.6rem,2vw,1.25rem)",
        background: "rgba(3,6,17,.86)",
        backdropFilter: "blur(14px)",
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-zoom-title"
        aria-describedby="link-zoom-subtitle"
        onSubmit={submit}
        style={{
          width: "min(540px,100%)",
          boxSizing: "border-box",
          maxHeight: "calc(100vh - 1.2rem)",
          overflowY: "auto",
          border: "1px solid rgba(0,212,255,.3)",
          borderRadius: "1.1rem",
          background: "linear-gradient(150deg,#101a28 0%,#0D1021 55%,#101927 100%)",
          boxShadow: "0 34px 105px rgba(0,0,0,.62), 0 0 0 1px rgba(255,255,255,.025) inset",
          padding: "clamp(1rem,2.5vw,1.5rem)",
        }}
      >
        <header style={{ display: "flex", alignItems: "flex-start", gap: ".8rem", marginBottom: "1.15rem" }}>
          <span
            style={{
              width: 42,
              height: 42,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              borderRadius: ".85rem",
              background: "rgba(0,212,255,.14)",
              border: "1px solid rgba(0,212,255,.28)",
              color: "#72E6F6",
            }}
          >
            <Video size={21} />
          </span>
          <div style={{ minWidth: 0 }}>
            <p
              style={{
                margin: "0 0 .22rem",
                color: "#72E6F6",
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "var(--chi-body-compact)",
                fontWeight: 800,
                letterSpacing: ".12em",
                textTransform: "uppercase",
              }}
            >
              Zoom
            </p>
            <h2
              id="link-zoom-title"
              style={{
                margin: 0,
                color: "white",
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "clamp(1.1rem,2vw,1.3rem)",
                letterSpacing: "-.025em",
              }}
            >
              Link a Zoom meeting
            </h2>
            <p id="link-zoom-subtitle" style={{ ...noteStyle, margin: ".3rem 0 0", fontSize: "var(--chi-body-compact)" }}>
              {eventTitle}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close without linking a meeting"
            onClick={onClose}
            disabled={linkMeeting.isPending}
            style={{
              marginLeft: "auto",
              width: 44,
              height: 44,
              // The flex row squeezes this to 39px wide at 390px without it.
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              border: "1px solid rgba(255,255,255,.1)",
              borderRadius: ".65rem",
              background: "rgba(255,255,255,.025)",
              color: "#AEB9C9",
              cursor: linkMeeting.isPending ? "default" : "pointer",
            }}
          >
            <X size={18} />
          </button>
        </header>

        {existing && (
          <section
            aria-label="Currently linked meeting"
            style={{
              padding: ".75rem .85rem",
              marginBottom: "1rem",
              border: "1px solid rgba(245,166,35,.24)",
              borderRadius: ".7rem",
              background: "rgba(245,166,35,.06)",
            }}
          >
            <p style={{ ...labelStyle, color: "#F5C06A", margin: 0 }}>Already linked</p>
            <p style={{ ...noteStyle, margin: ".35rem 0 0", color: "#C9D3E2" }}>
              {existing.zoomMeetingId
                ? `Meeting ${formatZoomMeetingId(existing.zoomMeetingId)}, added from Zoom.`
                : "An attendee link was entered by hand for this event."}{" "}
              Linking a meeting replaces it.
            </p>
          </section>
        )}

        <label htmlFor="zoom-meeting-id" style={labelStyle}>
          Zoom meeting ID
        </label>
        <input
          id="zoom-meeting-id"
          // Text rather than number: the ID is an identifier, and a number input
          // strips the spaces Zoom puts in it and adds a spinner that means nothing.
          type="text"
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          value={meetingIdInput}
          onChange={event => setMeetingIdInput(event.target.value)}
          placeholder="Enter the Zoom meeting ID"
          aria-describedby="zoom-meeting-id-help"
          aria-invalid={hasInput && !normalizedId ? true : undefined}
          style={{
            ...inputStyle,
            borderColor: hasInput && !normalizedId ? "rgba(248,113,113,.45)" : inputStyle.border.split(" ").pop(),
          }}
        />
        <p id="zoom-meeting-id-help" style={noteStyle}>
          {hasInput && !normalizedId
            ? "That is not a Zoom meeting ID. Enter 9 to 11 digits, or paste the meeting's join link."
            : normalizedId
              ? `Will link meeting ${formatZoomMeetingId(normalizedId)}.`
              : "Copy it from the Zoom meeting window, or paste the whole join link and the ID will be read from it."}
        </p>

        <p style={{ ...noteStyle, marginTop: ".9rem" }}>
          The attendee link is read from Zoom, so a meeting passcode stays intact. Only registered, signed-in
          attendees can retrieve it.
        </p>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: ".55rem", marginTop: "1.25rem" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={linkMeeting.isPending}
            style={{
              minHeight: 44,
              padding: ".6rem .95rem",
              border: "1px solid rgba(255,255,255,.12)",
              borderRadius: ".55rem",
              background: "transparent",
              color: "#AEB9C9",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "var(--chi-body-compact)",
              fontWeight: 700,
              cursor: linkMeeting.isPending ? "default" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!normalizedId || linkMeeting.isPending}
            style={{
              minHeight: 44,
              padding: ".6rem 1.1rem",
              border: 0,
              borderRadius: ".55rem",
              background: normalizedId ? "#0369A1" : "rgba(255,255,255,.06)",
              color: normalizedId ? "white" : "#64748B",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "var(--chi-body-compact)",
              fontWeight: 700,
              cursor: !normalizedId || linkMeeting.isPending ? "default" : "pointer",
              opacity: linkMeeting.isPending ? 0.7 : 1,
            }}
          >
            {linkMeeting.isPending ? "Linking…" : "Link meeting"}
          </button>
        </div>
      </form>
    </div>
  );
}
